import { useState } from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type GestureResponderEvent,
  type PointerEvent as NativePointerEvent
} from "react-native";
import Svg, { Defs, LinearGradient, Path, Line, Stop } from "react-native-svg";

import type {
  AdminDanceRetention,
  AdminDanceRetentionFamily
} from "@recital-master/shared";

import { FamilyWatchBreakdown } from "@/components/admin/FamilyWatchBreakdown";
import { retentionStepPath } from "@/components/admin/retentionPath";
import { Muted, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { RetentionStoryboardPreview } from "@/components/admin/RetentionStoryboardPreview";

const CHART_HEIGHT = 132;
const TOP_PAD = 10;
const PREVIEW_HALF_WIDTH = 88;
const PREVIEW_EDGE_GUTTER = 8;
const MINI_CHART_HEIGHT = 34;
const MINI_CHART_PAD = 3;
const PLAYBACK_BUCKET_MIDPOINT = 0.5;

function clampPlotX(value: number, width: number): number {
  return Math.max(0, Math.min(width, value));
}

function webPointerX(event: NativePointerEvent, width: number): number {
  const element = event.currentTarget as unknown as {
    getBoundingClientRect?: () => { left: number };
  };
  const rect = element.getBoundingClientRect?.();
  const value = rect
    ? event.nativeEvent.clientX - rect.left
    : event.nativeEvent.offsetX;
  return clampPlotX(value, width);
}

function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function watchMoments(bucket: { watchMoments?: number; viewers?: number }): number {
  return bucket.watchMoments ?? bucket.viewers ?? 0;
}

function fmtWatchPasses(value: number): string {
  return `${value} ${value === 1 ? "pass" : "passes"}`;
}

/**
 * One-second playback-pass area chart for one dance. Touching a second once
 * contributes one pass, so replaying a section creates a real upward spike.
 */
export function RetentionCurve({
  idleSummary,
  retention
}: {
  idleSummary?: string;
  retention: AdminDanceRetention;
}) {
  const [width, setWidth] = useState(0);
  const [selectedX, setSelectedX] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);

  const { durationSeconds, plays, buckets } = retention;
  if (plays === 0 || buckets.length === 0 || durationSeconds <= 0) {
    return (
      <View style={styles.empty}>
        <Muted variant="small">Retention appears after the first watches.</Muted>
      </View>
    );
  }

  const plotHeight = CHART_HEIGHT - TOP_PAD;
  const peakMoments = Math.max(1, plays, ...buckets.map(watchMoments));
  const x = (second: number) => (second / durationSeconds) * width;
  const y = (moments: number) =>
    TOP_PAD + (1 - Math.min(moments / peakMoments, 1)) * plotHeight;

  const points = buckets.map((bucket) => {
    const moments = watchMoments(bucket);
    return {
      boundaryX: x(bucket.startSecond),
      px: x(bucket.startSecond + PLAYBACK_BUCKET_MIDPOINT),
      py: y(moments),
      second: bucket.startSecond,
      watchMoments: moments
    };
  });

  const line = retentionStepPath(points, width);
  const area = `${line} L ${width} ${CHART_HEIGHT} L 0 ${CHART_HEIGHT} Z`;

  const inspectX = hoverX ?? selectedX;
  const inspectSecond = inspectX == null || width === 0
    ? null
    : Math.max(0, Math.min(durationSeconds, (inspectX / width) * durationSeconds));
  const scrubbed =
    inspectX == null || width === 0
      ? null
      : points.reduce((closest, p) =>
          Math.abs(p.px - inspectX) < Math.abs(closest.px - inspectX) ? p : closest
        );
  const previewLeft = hoverX == null
    ? null
    : Math.max(
        PREVIEW_HALF_WIDTH + PREVIEW_EDGE_GUTTER,
        Math.min(width - PREVIEW_HALF_WIDTH - PREVIEW_EDGE_GUTTER, hoverX)
      );

  const selectNativePoint = (event: GestureResponderEvent) => {
    setSelectedX(clampPlotX(event.nativeEvent.locationX, width));
  };

  return (
    <View>
      <View style={styles.readoutRow}>
        {scrubbed ? (
          <Text variant="smallStrong" color={palette.textSoft}>
            {fmtTime(inspectSecond ?? scrubbed.second)}
            <Muted variant="small">
              {"  ·  "}
              {fmtWatchPasses(scrubbed.watchMoments)} · {plays} playback starts
            </Muted>
          </Text>
        ) : (
          <Muted variant="small">
            {idleSummary ?? `${plays} ${plays === 1 ? "play" : "plays"}`}
          </Muted>
        )}
      </View>
      <Pressable
        accessibilityRole="adjustable"
        accessibilityLabel="Playback passes"
        accessibilityHint="Hover or select a point to inspect how often that moment was watched."
        accessibilityValue={inspectSecond == null ? undefined : {
          min: 0,
          max: Math.round(durationSeconds),
          now: Math.round(inspectSecond),
          text: `${fmtTime(inspectSecond)}, ${fmtWatchPasses(
            scrubbed?.watchMoments ?? 0
          )}, ${plays} playback starts`
        }}
        onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
        onPress={Platform.OS === "web" ? undefined : selectNativePoint}
        onPointerMove={Platform.OS === "web" ? (event) => {
          if (event.nativeEvent.pointerType === "mouse" || event.nativeEvent.pointerType === "pen") {
            setHoverX(webPointerX(event, width));
          }
        } : undefined}
        onPointerLeave={Platform.OS === "web" ? () => setHoverX(null) : undefined}
        onPointerUp={Platform.OS === "web" ? (event) => {
          setSelectedX(webPointerX(event, width));
        } : undefined}
        style={styles.plot}
      >
        {width > 0 ? (
          <Svg width={width} height={CHART_HEIGHT}>
            <Defs>
              <LinearGradient id="retentionFill" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={palette.accent} stopOpacity={0.28} />
                <Stop offset="1" stopColor={palette.accent} stopOpacity={0.02} />
              </LinearGradient>
            </Defs>
            <Path d={area} fill="url(#retentionFill)" />
            <Path
              d={line}
              stroke={palette.accent}
              strokeWidth={2}
              strokeLinejoin="round"
              fill="none"
            />
            {scrubbed ? (
              <Line
                x1={scrubbed.px}
                y1={TOP_PAD - 4}
                x2={scrubbed.px}
                y2={CHART_HEIGHT}
                stroke={palette.hairlineStrong}
                strokeWidth={1}
              />
            ) : null}
          </Svg>
        ) : null}
        {previewLeft != null && inspectSecond != null ? (
          <RetentionStoryboardPreview
            storyboardVttUrl={retention.storyboardVttUrl}
            time={inspectSecond}
            timeLabel={fmtTime(inspectSecond)}
            left={previewLeft}
          />
        ) : null}
      </Pressable>
      <View style={styles.axisRow}>
        <Muted variant="small">0:00</Muted>
        <Muted variant="small">{fmtTime(durationSeconds / 2)}</Muted>
        <Muted variant="small">{fmtTime(durationSeconds)}</Muted>
      </View>
    </View>
  );
}

export function WatchMomentSparkline({
  accessibilityLabel,
  buckets,
  durationSeconds,
  plays,
  storyboardVttUrl
}: {
  accessibilityLabel: string;
  buckets: AdminDanceRetentionFamily["buckets"];
  durationSeconds: number;
  plays: number;
  storyboardVttUrl: string | null;
}) {
  const [width, setWidth] = useState(0);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const plotHeight = MINI_CHART_HEIGHT - MINI_CHART_PAD * 2;
  const peakMoments = Math.max(1, plays, ...buckets.map(watchMoments));
  const points = buckets.map((bucket) => {
    const moments = watchMoments(bucket);
    return {
      boundaryX: durationSeconds > 0
        ? (bucket.startSecond / durationSeconds) * width
        : 0,
      px: durationSeconds > 0
        ? ((bucket.startSecond + PLAYBACK_BUCKET_MIDPOINT) / durationSeconds) * width
        : 0,
      py: MINI_CHART_PAD + (1 - Math.min(moments / peakMoments, 1)) * plotHeight
    };
  });
  const line = retentionStepPath(points, width);
  const area = line
    ? `${line} L ${width} ${MINI_CHART_HEIGHT} L 0 ${MINI_CHART_HEIGHT} Z`
    : "";
  const hoverSecond = hoverX == null || width === 0
    ? null
    : Math.max(0, Math.min(durationSeconds, (hoverX / width) * durationSeconds));
  const compactPreviewLeft = hoverX == null
    ? null
    : Math.max(72, Math.min(width - 72, hoverX));

  return (
    <Pressable
      accessibilityRole="image"
      accessibilityLabel={accessibilityLabel}
      onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
      onPointerMove={Platform.OS === "web" ? (event) => {
        if (event.nativeEvent.pointerType === "mouse" || event.nativeEvent.pointerType === "pen") {
          setHoverX(webPointerX(event, width));
        }
      } : undefined}
      onPointerLeave={Platform.OS === "web" ? () => setHoverX(null) : undefined}
      style={[styles.familySparkline, hoverX != null && styles.familySparklineActive]}
    >
      <View style={styles.familySparklinePlot}>
        {width > 0 && line ? (
          <Svg width={width} height={MINI_CHART_HEIGHT}>
            <Path d={area} fill={palette.accent} fillOpacity={0.1} />
            <Path
              d={line}
              stroke={palette.accent}
              strokeWidth={1.5}
              strokeLinejoin="round"
              fill="none"
            />
            {hoverX != null ? (
              <Line
                x1={hoverX}
                y1={0}
                x2={hoverX}
                y2={MINI_CHART_HEIGHT}
                stroke={palette.hairlineStrong}
                strokeWidth={1}
              />
            ) : null}
          </Svg>
        ) : null}
      </View>
      {compactPreviewLeft != null && hoverSecond != null ? (
        <RetentionStoryboardPreview
          compact
          storyboardVttUrl={storyboardVttUrl}
          time={hoverSecond}
          timeLabel={fmtTime(hoverSecond)}
          left={compactPreviewLeft}
          top={-104}
        />
      ) : null}
    </Pressable>
  );
}

/** Household-level versions of the aggregate curve, using identical buckets. */
export function RetentionFamilyBreakdown({
  compact,
  durationSeconds,
  families,
  storyboardVttUrl
}: {
  compact: boolean;
  durationSeconds: number;
  families: AdminDanceRetentionFamily[];
  storyboardVttUrl: string | null;
}) {
  if (families.length === 0) return null;
  return (
    <FamilyWatchBreakdown
      allowOverflow
      compact={compact}
      rows={families.map((family) => ({
        key: family.familyAccountId,
        email: family.email,
        summary: `${family.plays} ${family.plays === 1 ? "play" : "plays"}`,
        visual: (
          <WatchMomentSparkline
            accessibilityLabel={`${family.email}, ${family.plays} ${family.plays === 1 ? "play" : "plays"}, playback passes across the dance`}
            buckets={family.buckets}
            durationSeconds={durationSeconds}
            plays={family.plays}
            storyboardVttUrl={storyboardVttUrl}
          />
        )
      }))}
    />
  );
}

const styles = StyleSheet.create({
  empty: {
    alignItems: "center",
    paddingVertical: space.lg
  },
  readoutRow: {
    marginBottom: space.sm,
    minHeight: 18
  },
  plot: {
    backgroundColor: palette.coolWashFaint,
    borderRadius: radius.sm,
    height: CHART_HEIGHT,
    overflow: "hidden"
  },
  axisRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: space.xs
  },
  familySparkline: {
    flex: 1,
    height: MINI_CHART_HEIGHT,
    minWidth: 80,
    position: "relative"
  },
  familySparklineActive: {
    zIndex: 3
  },
  familySparklinePlot: {
    backgroundColor: palette.coolWashFaint,
    borderRadius: radius.sm,
    height: MINI_CHART_HEIGHT,
    overflow: "hidden",
    width: "100%"
  }
});
